import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';
import { getUnreadMessageCount } from '../utils/messageCount';
import '../styles/MobileTabBar.css';

const HomeGlyph = ({ active }) => (
  <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden>
    {active ? (
      <path
        fill="currentColor"
        d="M12.22 3.55 3.7 11.15c-.42.37-.15 1.05.43 1.05H6.1V20.4c0 .88.72 1.6 1.6 1.6h3.15v-6.35h4.3V22h3.15c.88 0 1.6-.72 1.6-1.6v-8.2h1.97c.58 0 .85-.68.43-1.05l-8.52-7.6a1.15 1.15 0 0 0-1.56 0Z"
      />
    ) : (
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="round"
        d="M4.35 12.05 13 4.45l8.65 7.6M7.2 11.15V20.55h3.55v-6.05h4.5v6.05H18.8V11.15"
      />
    )}
  </svg>
);

const BrowseGlyph = ({ active }) => (
  <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden>
    {active ? (
      <>
        <rect x="3.2" y="3.2" width="8.6" height="8.6" rx="2.4" fill="currentColor" />
        <rect x="14.2" y="3.2" width="8.6" height="8.6" rx="2.4" fill="currentColor" />
        <rect x="3.2" y="14.2" width="8.6" height="8.6" rx="2.4" fill="currentColor" />
        <rect x="14.2" y="14.2" width="8.6" height="8.6" rx="2.4" fill="currentColor" />
      </>
    ) : (
      <>
        <rect x="3.85" y="3.85" width="7.4" height="7.4" rx="2" fill="none" stroke="currentColor" strokeWidth="1.75" />
        <rect x="14.75" y="3.85" width="7.4" height="7.4" rx="2" fill="none" stroke="currentColor" strokeWidth="1.75" />
        <rect x="3.85" y="14.75" width="7.4" height="7.4" rx="2" fill="none" stroke="currentColor" strokeWidth="1.75" />
        <rect x="14.75" y="14.75" width="7.4" height="7.4" rx="2" fill="none" stroke="currentColor" strokeWidth="1.75" />
      </>
    )}
  </svg>
);

const InboxGlyph = ({ active }) => (
  <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden>
    {active ? (
      <path
        fill="currentColor"
        d="M5.1 4.2h15.8c1.27 0 2.3 1.03 2.3 2.3v9.85c0 1.27-1.03 2.3-2.3 2.3h-4.7l-2.55 2.85c-.4.45-1.15.45-1.55 0l-2.55-2.85H5.1c-1.27 0-2.3-1.03-2.3-2.3V6.5c0-1.27 1.03-2.3 2.3-2.3Z"
      />
    ) : (
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
        d="M5.35 5.05h15.3c.97 0 1.75.78 1.75 1.75v9.5c0 .97-.78 1.75-1.75 1.75h-4.85l-2.2 2.4c-.22.24-.6.24-.82 0l-2.2-2.4H5.35c-.97 0-1.75-.78-1.75-1.75V6.8c0-.97.78-1.75 1.75-1.75Z"
      />
    )}
  </svg>
);

const YouGlyph = ({ active }) => (
  <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden>
    {active ? (
      <>
        <circle cx="13" cy="8.15" r="3.85" fill="currentColor" />
        <path
          fill="currentColor"
          d="M4.85 21.55c.6-4.2 3.85-6.7 8.15-6.7s7.55 2.5 8.15 6.7c.08.52-.34 1.05-.87 1.05H5.72c-.53 0-.95-.53-.87-1.05Z"
        />
      </>
    ) : (
      <>
        <circle cx="13" cy="8.15" r="3.25" fill="none" stroke="currentColor" strokeWidth="1.75" />
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          d="M5.9 21.2c.75-3.7 3.5-5.7 7.1-5.7s6.35 2 7.1 5.7"
        />
      </>
    )}
  </svg>
);

const MobileTabBar = ({ browseOpen, onBrowse, hidden }) => {
  const location = useLocation();
  const loggedIn = isAuthenticated();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!loggedIn) {
      setUnread(0);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      const count = await getUnreadMessageCount();
      if (!cancelled) setUnread(count);
    };
    load();
    const interval = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [loggedIn, location.pathname]);

  if (hidden) return null;

  const path = location.pathname;
  const homeActive = path === '/';
  const inboxActive = path === '/messages' || path.startsWith('/profile/messages');
  const youActive = path.startsWith('/profile') && !inboxActive;
  const guestState = loggedIn ? undefined : { backgroundLocation: location };

  return (
    <nav className="mobile-tab-bar" aria-label="App">
      <Link to="/" className={`mobile-tab${homeActive && !browseOpen ? ' is-active' : ''}`}>
        <span className="mobile-tab-icon-wrap">
          <HomeGlyph active={homeActive && !browseOpen} />
        </span>
        <span>Home</span>
      </Link>
      <button
        type="button"
        className={`mobile-tab${browseOpen ? ' is-active' : ''}`}
        onClick={onBrowse}
      >
        <span className="mobile-tab-icon-wrap">
          <BrowseGlyph active={browseOpen} />
        </span>
        <span>Browse</span>
      </button>
      <Link
        to={loggedIn ? '/messages' : '/auth?signin=true'}
        state={guestState}
        className={`mobile-tab${inboxActive && !browseOpen ? ' is-active' : ''}`}
      >
        <span className="mobile-tab-icon-wrap">
          <InboxGlyph active={inboxActive && !browseOpen} />
          {unread > 0 ? (
            <span className="mobile-tab-badge">{unread > 9 ? '9+' : unread}</span>
          ) : null}
        </span>
        <span>Inbox</span>
      </Link>
      <Link
        to={loggedIn ? '/profile' : '/auth?signin=true'}
        state={guestState}
        className={`mobile-tab${youActive && !browseOpen ? ' is-active' : ''}`}
      >
        <span className="mobile-tab-icon-wrap">
          <YouGlyph active={youActive && !browseOpen} />
        </span>
        <span>You</span>
      </Link>
    </nav>
  );
};

export default MobileTabBar;
